import { ReactElement, useEffect, useState } from 'react'
import { AddContractStepProps, AddContractSteps, CustomerDataFormInputs } from '../../types'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import contractsStyles from '../../../Contracts.module.scss'
import styles from './CustomerData.module.scss'
import useIsComponentVisible from 'hooks/useIsComponentVisible'
import classNames from 'classnames'
import { LETTERS_NUMBERS_SLASHES_DASHES } from 'constants/regex'
import { hasNoNumber, isValidPostalCode, vatNumberToCompanyNumber } from 'utils/addContract'
import { GET_INITIAL_ADD_CONTRACT_DATA } from '../../constants'
import parse from 'html-react-parser'
import FormButtons from 'features/contracts/components/form-buttons/FormButtons'
import { Towns } from 'lib/types'
import { getTown } from 'lib/addContract'
import { checkIfProductIsDigitalOnly } from 'utils/products'
import { useGetProductContentQuery } from 'store/queries/cms-api'
import {
  Banner,
  Checkbox,
  convertDisplayDateToISO,
  Form,
  formatPhoneToE164,
  Heading,
  Icon,
  Select,
  TextField,
  Toggle,
  ToggleRadio
} from '@boltenergy-be/design-system'
import { ContractFlowTrackingTypes } from 'types/tracking.ts'
import { useStoreSelector } from 'hooks/store.ts'
import { useGetLanguage } from 'hooks/use-get-language'
import { validateBelgianEnterpriseNumber } from 'utils/validation.ts'
import { NormalizedAccount, NormalizedContact } from 'store/contact/types.ts'
import Phone from 'components/form-fields/phone/Phone.tsx'
import {
  CONTACT_MODES,
  getProductFromProductCode,
  LANGUAGES,
  LEGAL_FORMS,
  PAYMENT_METHODS,
  PRODUCT_CONFIG_OPTIONS,
  PRODUCTS_CONFIG
} from '@boltenergy-be/shared'
import { useLazyGetProductsQuery } from 'store/queries/bolt-api/products'
import Iban from 'components/form-fields/iban/Iban.tsx'
import { removeNonNumbers } from 'utils/format.ts'
import BirthDate from 'components/form-fields/birth-date/BirthDate.tsx'
import dayjs from 'dayjs'

const CustomerDataStep = ({ addContractData, setCurrentStep, setNextStep }: AddContractStepProps) => {
  // Redux
  const { accounts, billingContracts, contact } = useStoreSelector((store) => store.contact)
  const currentAccount = addContractData.customerData.customerNumber ? accounts[addContractData.customerData.customerNumber] : undefined
  const language = useGetLanguage()

  // Redux queries
  const { data: productsContent } = useGetProductContentQuery({ language })
  const [triggerGetApiProducts] = useLazyGetProductsQuery()

  // Local state
  const [account, setAccount] = useState<NormalizedAccount | undefined>(currentAccount)
  const [loading, setLoading] = useState<boolean>(false)
  const [towns, setTowns] = useState<Towns>([])

  // Constants
  const billingContract = account ? billingContracts[account.billingContracts[0]] : undefined
  const hasBillingContract = !!billingContract
  const product = getProductFromProductCode(addContractData[AddContractSteps.PRODUCT].electricity)
  const isDigitalOnly = product ? checkIfProductIsDigitalOnly(product)[PRODUCT_CONFIG_OPTIONS.billingOptions] : true
  const isDirectDebitOnly = product ? PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.directDebitMandatory] : true
  const newCustomerData = typeof account === 'undefined'
  const isProfessional = !!account?.company
  const hasCustomersInDropdown = !!Object.keys(accounts).length

  // React Hook Form
  const hookForm = useForm<CustomerDataFormInputs>({
    defaultValues: {
      ...addContractData[AddContractSteps.CUSTOMER_DATA],

      birthDate: addContractData[AddContractSteps.CUSTOMER_DATA]?.birthDate
        ? dayjs(addContractData[AddContractSteps.CUSTOMER_DATA].birthDate).format('DD/MM/YYYY')
        : '',
      // Set default deliveryMode on digital if it's a digital only product or Bolt Go simulationType
      contactMode: isDigitalOnly ? CONTACT_MODES.email : addContractData[AddContractSteps.CUSTOMER_DATA].contactMode,

      // Set default paymentMethod on digital if it's a digital only product
      paymentMethod: isDirectDebitOnly ? PAYMENT_METHODS.domiciliation : addContractData[AddContractSteps.CUSTOMER_DATA]?.paymentMethod
    }
  })
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    setValue,
    control
  } = hookForm
  const watchIsCompany = useWatch({ control, name: 'isCompany' })
  const watchPaymentMethod = useWatch({ control, name: 'paymentMethod' })
  const watchTownCode = useWatch({ control, name: 'correspondenceAddress.townCode' })
  const watchTownName = useWatch({ control, name: 'correspondenceAddress.townName' })
  const watchDeliveryMode = useWatch({ control, name: 'contactMode' })
  const watchSameCorrespondenceAddress = useWatch({ control, name: 'sameCorrespondenceAddress' })
  const watchPostalCode = useWatch({ control, name: 'correspondenceAddress.postalCode' })

  // The return of the constants
  const isIbanRequired = isDirectDebitOnly || watchPaymentMethod === PAYMENT_METHODS.domiciliation

  // i18n
  const { t } = useTranslation(['contracts', 'common', 'validation'])

  // Custom hook
  const { ref, isComponentVisible, setIsComponentVisible } = useIsComponentVisible(false)

  /**
   * Fetches towns when postalcode is present in registration data state object so towns are prefilled when navigating back
   */
  useEffect(() => {
    if ((hasBillingContract || addContractData.customerData.correspondenceAddress?.postalCode) && towns.length === 0) {
      const postalCode = hasBillingContract
        ? account?.correspondenceAddress?.postalCode
        : addContractData.customerData.correspondenceAddress?.postalCode
      void validatePostalCodeAndFetchTowns(postalCode!)
    }
  }, [addContractData, towns])

  /**
   * Generates the content for the customer dropdown item
   *
   * @param {NormalizedAccount} account
   * @returns {ReactElement}
   */
  const generateCustomerDropdownItemContent = (account: NormalizedAccount): ReactElement => {
    const isProfessional = !!account.company

    return (
      <div className={styles.user}>
        <span>
          {isProfessional && `${account.company?.name} - `}
          {account.firstName} {account.lastName}
        </span>
        <span>
          {account.customerNumber}{' '}
          {isProfessional &&
            `(${t(($) => $.company, {
              ns: 'common'
            })})`}
        </span>
      </div>
    )
  }

  /**
   * Generates the form values for a given customer
   *
   * @param {NormalizedAccount=} acc
   * @param {NormalizedContact=} contact
   * @returns {CustomerDataFormInputs}
   */
  const generateDefaultFormValuesForCustomer = (acc?: NormalizedAccount, contact?: NormalizedContact): CustomerDataFormInputs => {
    if (!acc) {
      const initialAddContractCustomerData = GET_INITIAL_ADD_CONTRACT_DATA(language).customerData
      return {
        ...initialAddContractCustomerData,
        customerNumber: undefined,
        email: contact?.email || '',
        emailConfirmation: contact?.email || '',
        phoneMobile: '',
        contactMode: CONTACT_MODES.email,
        correspondenceAddress: addContractData[AddContractSteps.ADDRESS].deliveryAddress,
        paymentMethod: PAYMENT_METHODS.domiciliation,
        iban: '',
        birthDate: initialAddContractCustomerData?.birthDate ? dayjs(initialAddContractCustomerData.birthDate).format('DD/MM/YYYY') : ''
      }
    }

    const isCompany = !!acc.company

    if (acc.correspondenceAddress?.postalCode && !isNaN(Number(acc.correspondenceAddress?.postalCode))) {
      void validatePostalCodeAndFetchTowns(Number(acc.correspondenceAddress.postalCode))
    }

    return {
      customerNumber: acc.customerNumber,
      isCompany,
      company: isCompany
        ? {
            enterpriseNumber: vatNumberToCompanyNumber(acc.company?.enterpriseNumber ?? ''),
            legalForm: acc.company?.legalForm ?? LEGAL_FORMS.srl_bv,
            name: acc.company?.name || '',
            vatApplication: typeof acc.company?.vatApplication === 'boolean' ? acc.company?.vatApplication : true
          }
        : undefined,
      firstName: acc.firstName,
      lastName: acc.lastName,
      email: contact?.email || '',
      emailConfirmation: contact?.email || '',
      phoneMobile: formatPhoneToE164(contact?.phone || addContractData[AddContractSteps.CUSTOMER_DATA].phoneMobile),
      birthDate: acc.birthDate ? dayjs(acc.birthDate).format('DD/MM/YYYY') : '',
      language: contact?.language || language,
      correspondenceAddress: {
        streetName: acc?.correspondenceAddress.streetName ?? '',
        streetNumber: acc?.correspondenceAddress.streetNumber ?? '',
        streetBox: acc?.correspondenceAddress.streetBox ?? '',
        postalCode: acc?.correspondenceAddress.postalCode ?? '',
        townCode: watchTownCode || 0,
        townName: acc?.correspondenceAddress.townName ?? '',
        countryCode: 'BE'
      },
      paymentMethod: acc?.paymentDetails?.directDebit ? PAYMENT_METHODS.domiciliation : PAYMENT_METHODS.transfer,
      iban: acc?.paymentDetails?.iban,
      contactMode: billingContract?.deliveryMode ?? CONTACT_MODES.email
    }
  }

  /**
   * Handles the customer button click
   *
   * @param {NormalizedAccount|undefined} acc
   */
  const handleCustomerButtonClick = (acc?: NormalizedAccount) => {
    setAccount(acc)
    setIsComponentVisible(false)
    setValue('sameCorrespondenceAddress', false)

    reset(generateDefaultFormValuesForCustomer(acc, contact))
  }

  /**
   * Handles the town select change & sets the town code
   * TODO: write custom hook for this (same as in AddressStep.tsx)
   *
   * @param {string} selectedTown
   */
  const handleTownSelectChange = (selectedTown: string) => {
    const town = towns.find((town) => town.townName === selectedTown)
    const townName = town?.townName ?? ''

    setValue('correspondenceAddress.townName', townName)
  }

  /**
   * Handles the submit after validation by React Hook Form
   *
   * @param {CustomerDataFormInputs} data
   */
  const onSubmit = (data: CustomerDataFormInputs) => {
    setLoading(true)

    void triggerGetApiProducts()
    setNextStep(
      {
        ...data,
        isCompany: !data.isCompany && !account ? false : data.isCompany,
        birthDate: !data.isCompany ? convertDisplayDateToISO(data.birthDate) : ''
      },
      AddContractSteps.CONFIRMATION
    )

    setLoading(false)
  }

  /**
   * Fetches the town names based on the given postalCode
   * TODO: write custom hook for this (same as in AddressStep.tsx)
   *
   * @param {number} postalCode
   */
  const validatePostalCodeAndFetchTowns = async (postalCode: number) => {
    if (isValidPostalCode(postalCode)) {
      const response = await getTown(postalCode)

      if (response !== null) {
        const { towns } = response

        setTowns(towns)

        if (
          !towns.some(
            (town) => town.townName === (addContractData[AddContractSteps.CUSTOMER_DATA].correspondenceAddress?.townName || watchTownName)
          )
        ) {
          setValue('correspondenceAddress.townName', towns[0].townName)
        }

        return true
      }
    }

    return false
  }

  return (
    <section className={styles['customer-data']}>
      <Heading as="h1" variant="h4">
        {t(($) => $.add.steps.customerData.title)}
      </Heading>
      <Banner type="informative">{t(($) => $.add.steps.customerData.info)}</Banner>

      <FormProvider {...hookForm}>
        <Form onSubmit={handleSubmit(onSubmit)} className={contractsStyles['narrow-grid']}>
          {/* CUSTOMER DROPDOWN */}
          {hasCustomersInDropdown && (
            <>
              <div className={styles['customer-dropdown']}>
                <div className={styles['button-container']}>
                  <button
                    className={styles['selected-customer']}
                    onClick={(e) => {
                      e.preventDefault()
                      setIsComponentVisible(true)
                    }}
                  >
                    {account ? generateCustomerDropdownItemContent(account) : t(($) => $.add.steps.customerData.customerSelect.newCustomer)}
                    <Icon name="chevronDown" />
                  </button>
                </div>

                {isComponentVisible && (
                  <>
                    <div ref={ref} className={styles['dropdown-container']}>
                      <div>
                        {Object.values(accounts).map((acc, index) => (
                          <button
                            key={index}
                            className={classNames(styles['customer-button'], {
                              [styles.active]: account?.customerNumber === acc.customerNumber
                            })}
                            onClick={() => handleCustomerButtonClick(acc)}
                          >
                            {generateCustomerDropdownItemContent(acc)}
                          </button>
                        ))}

                        <button
                          className={classNames(styles['new-customer-button'], { [styles.active]: account === undefined })}
                          onClick={(e) => {
                            e.preventDefault()
                            handleCustomerButtonClick(undefined)
                          }}
                        >
                          + {t(($) => $.add.steps.customerData.customerSelect.newCustomer)}
                        </button>
                      </div>
                    </div>
                    <div className="overlay dropdown-overlay" />
                  </>
                )}
              </div>
            </>
          )}

          {newCustomerData && (
            <Checkbox
              {...register('isCompany')}
              className="grid-col-full"
              id="isCompany"
              label={t(($) => $.add.steps.customerData.fields.registerAsCompany)}
            />
          )}

          {(watchIsCompany || isProfessional) && (
            <>
              <Heading as="h2" variant="h4" className={classNames(contractsStyles['label-large'], 'grid-col-full')}>
                {t(($) => $.add.steps.customerData.sections.company)}
              </Heading>

              {/* COMPANY NAME */}
              <TextField
                grid="full"
                id="companyName"
                label={t(($) => $.add.steps.customerData.fields.companyName)}
                {...register('company.name', {
                  required: t(($) => $.required, {
                    ns: 'validation'
                  }),
                  maxLength: {
                    value: 100,
                    message: t(($) => $.invalid.length, {
                      length: 100,
                      ns: 'validation'
                    })
                  }
                })}
                disabled={!newCustomerData}
                error={errors?.company?.name?.message}
              />

              {/* COMPANY VAT NUMBER */}
              <TextField
                grid="full"
                id="companyNumber"
                label={t(($) => $.add.steps.customerData.fields.companyNumber)}
                {...register('company.enterpriseNumber', {
                  required: t(($) => $.required, {
                    ns: 'validation'
                  }),
                  validate: (value) =>
                    validateBelgianEnterpriseNumber(value!) ||
                    t(($) => $.invalid.invalidCompanyNumber, {
                      ns: 'validation'
                    })
                })}
                disabled={!newCustomerData}
                placeholder="0123456789"
                error={errors?.company?.enterpriseNumber?.message}
                onBlur={(event) => setValue('company.enterpriseNumber', removeNonNumbers(event.target.value), { shouldValidate: true })}
              />

              {/* COMPANY LEGAL FORM */}
              <Select
                grid="full"
                id="legalForm"
                label={t(($) => $.add.steps.customerData.fields.legalForm)}
                {...register('company.legalForm', {
                  required: t(($) => $.required, {
                    ns: 'validation'
                  })
                })}
                disabled={!newCustomerData}
                error={errors?.company?.legalForm?.message}
              >
                <option value="" disabled>
                  {t(($) => $.defaultPlaceholders.select, {
                    ns: 'common'
                  })}
                </option>
                {Object.values(LEGAL_FORMS)
                  .sort((a, b) => t(($) => $.legalForms[a], { ns: 'common' }).localeCompare(t(($) => $.legalForms[b], { ns: 'common' })))
                  .map((legalForm) => (
                    <option key={legalForm} value={legalForm}>
                      {t(($) => $.legalForms[legalForm], { ns: 'common' })}
                    </option>
                  ))}
              </Select>

              {/* COMPANY SUBJECT TO VAT */}
              <Checkbox
                {...register('company.vatApplication')}
                className="grid-col-full"
                id="vatApplication"
                label={t(($) => $.add.steps.customerData.fields.subjectToVat)}
                disabled={!newCustomerData}
              />
            </>
          )}

          <Heading as="h2" variant="h4" className="grid-col-full">
            {t(($) => $.add.steps.customerData.sections.personal)}
          </Heading>

          {/* FIRST NAME */}
          <TextField
            id="firstName"
            label={t(($) => $.add.steps.customerData.fields.firstName)}
            {...register('firstName', {
              required: t(($) => $.required, {
                ns: 'validation'
              }),
              minLength: {
                value: 2,
                message: t(($) => $.min, {
                  min: 2,
                  ns: 'validation'
                })
              },
              validate: (value) =>
                hasNoNumber(value) ||
                t(($) => $.noNumbers, {
                  defaultValue: '',
                  attribute: t(($) => $.add.steps.customerData.fields.firstName),
                  ns: 'validation'
                })
            })}
            error={errors?.firstName?.message}
            disabled={!newCustomerData}
            grid="span 1"
          />

          {/* LAST NAME */}
          <TextField
            id="lastName"
            label={t(($) => $.add.steps.customerData.fields.lastName)}
            {...register('lastName', {
              required: t(($) => $.required, {
                ns: 'validation'
              }),
              validate: (value) =>
                hasNoNumber(value) ||
                t(($) => $.noNumbers, {
                  defaultValue: '',
                  attribute: t(($) => $.add.steps.customerData.fields.lastName),
                  ns: 'validation'
                })
            })}
            error={errors?.lastName?.message}
            disabled={!newCustomerData}
            grid="span 1"
          />

          {/* EMAIL */}
          <TextField
            grid="full"
            id="email"
            label={t(($) => $.add.steps.customerData.fields.email)}
            {...register('email')}
            readOnly
            disabled
          />

          {/* EMAIL CONFIRMATION */}
          <TextField
            grid="full"
            id="emailConfirmation"
            label={t(($) => $.add.steps.customerData.fields.emailConfirmation)}
            {...register('emailConfirmation')}
            disabled
            readOnly
          />

          {newCustomerData && (
            <Banner type="informative" className={contractsStyles['alert-container']}>
              <p>{parse(t(($) => $.add.steps.customerData.fields.emailInfo))}</p>
            </Banner>
          )}

          {/* MOBILE PHONE */}
          <Phone grid="full" label={t(($) => $.add.steps.customerData.fields.mobilePhone)} disabled={!newCustomerData} />

          {/* LANGUAGE */}
          <Select
            grid="full"
            id="language"
            label={t(($) => $.add.steps.customerData.fields.language.label)}
            {...register('language')}
            disabled={!newCustomerData}
          >
            <option value={LANGUAGES.NL}>{t(($) => $.add.steps.customerData.fields.language.dutch)}</option>
            <option value={LANGUAGES.FR}>{t(($) => $.add.steps.customerData.fields.language.french)}</option>
          </Select>

          {(newCustomerData && !watchIsCompany) || (!newCustomerData && !isProfessional) ? (
            <FormProvider {...hookForm}>
              <BirthDate disabled={!newCustomerData} />
            </FormProvider>
          ) : (
            <Form.EmptyField />
          )}

          <Heading as="h2" variant="h4" className={classNames(contractsStyles['label-large'], 'grid-col-full')}>
            {t(($) => $.add.steps.customerData.sections.correspondenceAddress)}
          </Heading>

          <Checkbox
            {...register('sameCorrespondenceAddress')}
            className="grid-col-full"
            id="sameCorrespondenceAddress"
            label={t(($) => $.add.steps.customerData.fields.sameCorrespondenceAddress)}
          />

          {!watchSameCorrespondenceAddress && (
            <>
              {/* CORRESPONDENCE ADDRESS STREET */}
              <TextField
                id="correspondenceAddress.streetName"
                label={t(($) => $.add.steps.customerData.fields.correspondenceAddress.streetName)}
                {...register('correspondenceAddress.streetName', {
                  required: t(($) => $.required, {
                    ns: 'validation'
                  })
                })}
                error={errors?.correspondenceAddress?.streetName?.message}
              />

              <div className={styles.subgrid}>
                {/* CORRESPONDENCE ADDRESS STREET NUMBER */}
                <TextField
                  id="correspondenceAddress.streetNumber"
                  label={t(($) => $.add.steps.customerData.fields.correspondenceAddress.streetNumber)}
                  {...register('correspondenceAddress.streetNumber', {
                    required: t(($) => $.required, {
                      ns: 'validation'
                    }),
                    maxLength: {
                      value: 5,
                      message: t(($) => $.invalid.streetNumber, {
                        ns: 'validation'
                      })
                    },
                    pattern: {
                      value: LETTERS_NUMBERS_SLASHES_DASHES,
                      message: t(($) => $.invalid.streetNumber, {
                        ns: 'validation'
                      })
                    }
                  })}
                  error={errors?.correspondenceAddress?.streetNumber?.message}
                />

                {/* CORRESPONDENCE ADDRESS STREET BOX */}
                <TextField
                  id="correspondenceAddress.streetBox"
                  label={t(($) => $.add.steps.customerData.fields.correspondenceAddress.streetBox)}
                  {...register('correspondenceAddress.streetBox', {
                    maxLength: {
                      value: 5,
                      message: t(($) => $.invalid.length, {
                        length: 5,
                        ns: 'validation'
                      })
                    },
                    pattern: {
                      value: LETTERS_NUMBERS_SLASHES_DASHES,
                      message: t(($) => $.invalid.streetBox, {
                        ns: 'validation'
                      })
                    }
                  })}
                  maxLength={5}
                  error={errors?.correspondenceAddress?.streetBox?.message}
                />
              </div>

              {/* CORRESPONDENCE ADDRESS POSTAL CODE */}
              <TextField
                id="correspondenceAddress.postalCode"
                label={t(($) => $.add.steps.customerData.fields.correspondenceAddress.postalCode)}
                {...register('correspondenceAddress.postalCode', {
                  required: t(($) => $.required, {
                    ns: 'validation'
                  }),
                  validate: (val) => validatePostalCodeAndFetchTowns(Number(val))
                })}
                type="number"
                onChange={(e) => {
                  const value = e.target.valueAsNumber
                  setValue('correspondenceAddress.postalCode', value)
                  void validatePostalCodeAndFetchTowns(value)
                }}
                inputMode="numeric"
                error={errors?.correspondenceAddress?.postalCode?.message}
              />

              {/* CORRESPONDENCE ADDRESS TOWN NAME */}
              <Select
                id="correspondenceAddress.townName"
                label={t(($) => $.add.steps.customerData.fields.correspondenceAddress.townName)}
                {...register('correspondenceAddress.townName', {
                  required: t(($) => $.required, {
                    ns: 'validation'
                  })
                })}
                value={watchTownName}
                error={errors?.correspondenceAddress?.townName?.message}
                onChange={(e) => handleTownSelectChange(e.target.value)}
              >
                <option disabled>
                  {!watchPostalCode
                    ? t(($) => $.add.steps.customerData.fields.correspondenceAddress.townNamePlaceholder)
                    : t(($) => $.defaultPlaceholders.select, {
                        ns: 'common'
                      })}
                </option>
                {towns.map((town) => (
                  <option key={`town.geoCode-${town.townCode}`} value={town.townName}>
                    {town.townName}
                  </option>
                ))}
              </Select>
            </>
          )}

          <fieldset>
            {/* DELIVERY MODE */}
            <legend className={contractsStyles['fieldset-legend']}>
              {isDigitalOnly
                ? t(($) => $.add.steps.customerData.deliveryMode.digitalOnlyLabel)
                : t(($) => $.add.steps.customerData.deliveryMode.label)}
            </legend>

            {isDigitalOnly ? (
              <p className={styles['no-margin']}>
                {t(($) => $.add.steps.customerData.deliveryMode.digitalOnly, {
                  product: product ? productsContent?.[product]?.name : ''
                })}
                <button type="button" className={styles['inline-btn']} onClick={() => setCurrentStep(AddContractSteps.PRODUCT)}>
                  {t(($) => $.add.steps.customerData.chooseAnotherTariff, {
                    defaultValue: 'ander tarief kiezen'
                  })}
                </button>
                .
              </p>
            ) : (
              <Toggle
                isFullwidth
                active={watchDeliveryMode}
                options={[
                  {
                    value: CONTACT_MODES.email,
                    label: t(($) => $.add.steps.customerData.fields.deliveryMode.digital)
                  },
                  {
                    value: CONTACT_MODES.post,
                    label: t(($) => $.add.steps.customerData.fields.deliveryMode.paper)
                  }
                ]}
                onClick={(value) => setValue('contactMode', value)}
              />
            )}
          </fieldset>

          {/* PAYMENT METHOD */}
          {!isDirectDebitOnly && (
            <ToggleRadio
              grid="full"
              name="paymentMethod"
              label={
                isDirectDebitOnly
                  ? t(($) => $.add.steps.customerData.paymentMethod.directDebitOnlyLabel)
                  : t(($) => $.add.steps.customerData.paymentMethod.label)
              }
              options={[
                {
                  ...register('paymentMethod'),
                  value: PAYMENT_METHODS.domiciliation,
                  id: PAYMENT_METHODS.domiciliation,
                  label: t(($) => $.add.steps.customerData.fields.paymentMethod.directDebit)
                },
                {
                  ...register('paymentMethod'),
                  value: PAYMENT_METHODS.transfer,
                  id: PAYMENT_METHODS.transfer,
                  label: t(($) => $.add.steps.customerData.fields.paymentMethod.bankTransfer)
                }
              ]}
            />
          )}

          {/* IBAN */}
          <Iban
            {...(!isIbanRequired && { description: t(($) => $.add.steps.customerData.fields.refundDescription) })}
            required={isIbanRequired}
          />

          {isDirectDebitOnly && (
            <p className="grid-col-full">
              {t(($) => $.add.steps.customerData.paymentMethod.directDebitOnly, {
                product: product ? productsContent?.[product]?.name : ''
              })}
              <button type="button" className={styles['inline-btn']} onClick={() => setCurrentStep(AddContractSteps.PRODUCT)}>
                {t(($) => $.add.steps.customerData.chooseAnotherTariff)}
              </button>
              .
            </p>
          )}

          <small className={contractsStyles.description}>{t(($) => $.add.steps.customerData.fields.mandateLegal)}</small>

          <FormButtons currentStep={AddContractSteps.CUSTOMER_DATA} loading={loading} trackingId={ContractFlowTrackingTypes.ADD} />
        </Form>
      </FormProvider>
    </section>
  )
}

export default CustomerDataStep
